/**
 * 序列化装饰器
 *
 * 提供组件级别的序列化支持，包括字段级装饰器和类级装饰器
 */
/**
 * 序列化元数据的Symbol键
 */
export const SERIALIZABLE_METADATA = Symbol('SerializableMetadata');
export const SERIALIZE_FIELD = Symbol('SerializeField');
export const SERIALIZE_OPTIONS = Symbol('SerializeOptions');
/**
 * 组件可序列化装饰器
 *
 * 标记组件类为可序列化，必须与字段装饰器配合使用
 *
 * @param options 序列化配置选项
 *
 * @example
 * ```typescript
 * @ECSComponent('Player')
 * @Serializable({ version: 1 })
 * class PlayerComponent extends Component {
 *     @Serialize() name: string = 'Player';
 *     @Serialize() level: number = 1;
 * }
 * ```
 */
export function Serializable(options) {
    return function (target) {
        if (!options || typeof options.version !== 'number') {
            throw new Error('Serializable装饰器必须提供有效的版本号');
        }
        // 检查是否有自己的元数据（不是从父类继承的）
        const hasOwnMetadata = Object.prototype.hasOwnProperty.call(target, SERIALIZABLE_METADATA);
        let metadata;
        if (hasOwnMetadata) {
            // 已有自己的元数据，更新 options
            metadata = target[SERIALIZABLE_METADATA];
            metadata.options = options;
        }
        else {
            // 没有自己的元数据，检查是否有继承的元数据
            const inheritedMetadata = target[SERIALIZABLE_METADATA];
            // 创建新的元数据对象（从继承的元数据复制字段，但使用新的 options）
            metadata = {
                options,
                fields: inheritedMetadata ? new Map(inheritedMetadata.fields) : new Map(),
                ignoredFields: inheritedMetadata ? new Set(inheritedMetadata.ignoredFields) : new Set()
            };
            target[SERIALIZABLE_METADATA] = metadata;
        }
        return target;
    };
}
/**
 * 字段序列化装饰器
 *
 * 标记字段为可序列化
 *
 * @param options 字段序列化选项（可选）
 *
 * @example
 * ```typescript
 * @Serialize()
 * name: string = 'Player';
 *
 * @Serialize({ alias: 'hp' })
 * health: number = 100;
 * ```
 */
export function Serialize(options) {
    return function (target, propertyKey) {
        const constructor = target.constructor;
        // 检查是否有自己的元数据（不是从父类继承的）
        const hasOwnMetadata = Object.prototype.hasOwnProperty.call(constructor, SERIALIZABLE_METADATA);
        let metadata;
        if (hasOwnMetadata) {
            // 已有自己的元数据
            metadata = constructor[SERIALIZABLE_METADATA];
        }
        else {
            // 没有自己的元数据，检查是否有继承的元数据
            const inheritedMetadata = constructor[SERIALIZABLE_METADATA];
            // 创建新的元数据对象（从继承的元数据复制）
            metadata = {
                options: inheritedMetadata ? { ...inheritedMetadata.options } : { version: 1 },
                fields: inheritedMetadata ? new Map(inheritedMetadata.fields) : new Map(),
                ignoredFields: inheritedMetadata ? new Set(inheritedMetadata.ignoredFields) : new Set()
            };
            constructor[SERIALIZABLE_METADATA] = metadata;
        }
        // 记录字段
        metadata.fields.set(propertyKey, options || {});
    };
}
/**
 * Map序列化装饰器
 *
 * 专门用于序列化Map类型字段
 *
 * @example
 * ```typescript
 * @SerializeAsMap()
 * inventory: Map<string, number> = new Map();
 * ```
 */
export function SerializeAsMap() {
    return function (target, propertyKey) {
        Serialize({
            serializer: (value) => {
                if (!(value instanceof Map)) {
                    return null;
                }
                return Array.from(value.entries());
            },
            deserializer: (value) => {
                if (!Array.isArray(value)) {
                    return new Map();
                }
                return new Map(value);
            }
        })(target, propertyKey);
    };
}
/**
 * Set序列化装饰器
 *
 * 专门用于序列化Set类型字段
 *
 * @example
 * ```typescript
 * @SerializeAsSet()
 * tags: Set<string> = new Set();
 * ```
 */
export function SerializeAsSet() {
    return function (target, propertyKey) {
        Serialize({
            serializer: (value) => {
                if (!(value instanceof Set)) {
                    return null;
                }
                return Array.from(value);
            },
            deserializer: (value) => {
                if (!Array.isArray(value)) {
                    return new Set();
                }
                return new Set(value);
            }
        })(target, propertyKey);
    };
}
/**
 * 忽略序列化装饰器
 *
 * 标记字段不参与序列化
 *
 * @example
 * ```typescript
 * @IgnoreSerialization()
 * tempCache: any = null;
 * ```
 */
export function IgnoreSerialization() {
    return function (target, propertyKey) {
        const constructor = target.constructor;
        // 检查是否有自己的元数据（不是从父类继承的）
        const hasOwnMetadata = Object.prototype.hasOwnProperty.call(constructor, SERIALIZABLE_METADATA);
        let metadata;
        if (hasOwnMetadata) {
            // 已有自己的元数据
            metadata = constructor[SERIALIZABLE_METADATA];
        }
        else {
            // 没有自己的元数据，检查是否有继承的元数据
            const inheritedMetadata = constructor[SERIALIZABLE_METADATA];
            // 创建新的元数据对象（从继承的元数据复制）
            metadata = {
                options: inheritedMetadata ? { ...inheritedMetadata.options } : { version: 1 },
                fields: inheritedMetadata ? new Map(inheritedMetadata.fields) : new Map(),
                ignoredFields: inheritedMetadata ? new Set(inheritedMetadata.ignoredFields) : new Set()
            };
            constructor[SERIALIZABLE_METADATA] = metadata;
        }
        // 记录忽略字段
        metadata.ignoredFields.add(propertyKey);
    };
}
/**
 * 获取组件的序列化元数据
 *
 * @param componentClass 组件类或组件实例
 * @returns 序列化元数据，如果组件不可序列化则返回null
 */
export function getSerializationMetadata(componentClass) {
    if (!componentClass) {
        return null;
    }
    // 如果是实例，获取其构造函数
    const constructor = typeof componentClass === 'function'
        ? componentClass
        : componentClass.constructor;
    return constructor[SERIALIZABLE_METADATA] || null;
}
/**
 * 检查组件是否可序列化
 *
 * @param component 组件类或组件实例
 * @returns 如果组件可序列化返回true
 */
export function isSerializable(component) {
    return getSerializationMetadata(component) !== null;
}
//# sourceMappingURL=SerializationDecorators.js.map